export class Quad<T> {
    constructor(public capacity = 4) {}

    public points = new Array<T>();
}

export interface IPoint {
    x: number;
    y: number;
}

export interface IBoundary {
    xmin: number;
    xmax: number;
    ymin: number;
    ymax: number;
}

export class Boundary implements IBoundary {
    constructor(public xmin: number, public xmax: number, public ymin: number, public ymax: number) {}

    boundaryContains(point: IPoint) {
        return this.xmin <= point.x && this.xmax >= point.x && this.ymin <= point.y && this.ymax >= point.y;
    }

    intersects(b: Boundary) {
        if (this.xmin >= b.xmax || b.xmin >= this.xmax || this.ymin >= b.ymax || b.ymin >= this.ymax) {
            return false;
        }

        return true;
    }

    get halfPoint() {
        return {
            x: this.xmin + this.width / 2,
            y: this.ymin + this.height / 2,
        } as IPoint;
    }

    get width() {
        return this.xmax - this.xmin;
    }
    get height() {
        return this.ymax - this.ymin;
    }
}

export interface IQuadTreeOptions<T> extends IBoundary {
    items: T[];
    xSelector: (item: T) => number;
    ySelector: (item: T) => number;
    capacity: number;
}

interface IProximityItem<T> {
    item: T;
    proximity: number;
}

export class QuadTree<T> extends Boundary {
    constructor(public o: IQuadTreeOptions<T>) {
        super(o.xmin, o.xmax, o.ymin, o.ymax);

        this.xSelector = o.xSelector;
        this.ySelector = o.ySelector;
        this.capacity = o.capacity;

        if (o.items) {
            for (const item of o.items) this.add(item);
        }
    }

    public items = new Array<T>();
    private size = 0;
    private duplicatesMap: Record<string, T> = {};
    public xSelector: (item: T) => number;
    public ySelector: (item: T) => number;
    public capacity: number;

    public nw: QuadTree<T>;
    public ne: QuadTree<T>;
    public sw: QuadTree<T>;
    public se: QuadTree<T>;

    private _divided = false;
    public get divided() {
        return this._divided;
    }

    public get quads() {
        const arr = Array<QuadTree<T>>();
        if (this.nw) arr.push(this.nw);
        if (this.ne) arr.push(this.ne);
        if (this.sw) arr.push(this.sw);
        if (this.se) arr.push(this.se);
        return arr;
    }

    public find(point: IPoint, radius: number): Array<IProximityItem<T>> {
        const b = new Boundary(point.x - radius, point.x + radius, point.y - radius, point.y + radius);
        const results = this.findByBoundary(b);

        //sort by proximity
        results.sortBy(r => r.proximity);
        return results;
    }

    public findByBoundary(boundary: Boundary): Array<IProximityItem<T>> {
        const results = new Array<IProximityItem<T>>();

        if (!this.intersects(boundary)) {
            return results;
        }

        if (this.divided) {
            for (const q of this.quads) {
                results.push(...q.findByBoundary(boundary));
            }
        } else {
            //check items at this level
            for (const i of this.items) {
                if (boundary.boundaryContains(this.itemPoint(i))) {
                    results.push(this.getWithProximity(i, boundary.halfPoint));
                }
            }
        }

        return results;
    }

    protected getWithProximity(item: T, searchPoint: IPoint): IProximityItem<T> {
        const itemPoint = this.itemPoint(item);
        return {
            item: item,
            proximity: Math.sqrt(Math.pow(searchPoint.x - itemPoint.x, 2) + Math.pow(searchPoint.y - itemPoint.y, 2)),
        };
    }

    protected positionKey(point: { x: number; y: number }) {
        return point.x + ',' + point.y;
    }

    public add(item: T): boolean {
        const point = this.itemPoint(item);
        const key = this.positionKey(point);
        if (!this.boundaryContains(point)) {
            return false;
        }
        // If there is another point in this quad with the **exact** same position, add it to this quad as well.
        // This may result in overfilling the quad but there's no other way to handle exact duplicate points
        if (this.duplicatesMap[key]) {
            this.items.push(item);
            return true;
        }

        //if there is space for it, and no sub-divisions, add it to this quad
        if (this.size < this.o.capacity && !this.divided) {
            this.items.push(item);
            this.duplicatesMap[key] = item;
            this.size++;
            return true;
        }

        if (!this.divided) {
            this.subdivide();
        }

        for (const q of this.quads) {
            if (q.add(item)) return true;
        }

        throw Error('unexpected error creating quadtree');
    }

    protected subdivide() {
        // No need to check y, if x is infinitesimally small then y is as well
        if (this.xmax - this.xmin <= Number.EPSILON * 2) {
            throw Error('unexpected error creating quadtree. Not enough granularity');
        }

        this.nw = this.createDivision({
            items: this.items,
            xmin: this.xmin,
            xmax: this.halfPoint.x,
            ymin: this.ymin,
            ymax: this.halfPoint.y,
        });
        this.ne = this.createDivision({
            items: this.items,
            xmin: this.halfPoint.x + Number.EPSILON,
            xmax: this.xmax,
            ymin: this.ymin,
            ymax: this.halfPoint.y,
        });
        this.sw = this.createDivision({
            items: this.items,
            xmin: this.xmin,
            xmax: this.halfPoint.x,
            ymin: this.halfPoint.y + Number.EPSILON,
            ymax: this.ymax,
        });
        this.se = this.createDivision({
            items: this.items,
            xmin: this.halfPoint.x + Number.EPSILON,
            xmax: this.xmax,
            ymin: this.halfPoint.y + Number.EPSILON,
            ymax: this.ymax,
        });

        this.items.clear();
        this.size = 0;
        this._divided = true;
    }

    protected itemPoint(item: T): IPoint {
        return {
            x: this.xSelector(item),
            y: this.ySelector(item),
        };
    }

    protected createDivision(options: Partial<IQuadTreeOptions<T>>) {
        return new QuadTree<T>({
            ...this.o,
            ...options,
        });
    }
}
